"use client";

import { Check, DollarSign, Send, X } from "lucide-react";
import { makeFunctionReference } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuerySkeleton } from "@/components/ui/skeleton";
import {
  type CarrierPortalDocumentType,
  buildCarrierDocumentUploadDraft,
} from "@/core/portal/carrier-documents";
import {
  buildCarrierOfferResponseDraft,
  formatCarrierPay,
  type CarrierOffer,
  type CarrierOfferResponseType,
} from "@/core/portal/carrier-offer";

interface CarrierOfferQueryResult {
  offer: {
    token: string;
    carrierName: string;
    carrierMc?: string;
    carrierEmail?: string;
    carrierPayCents: number;
    status: CarrierOffer["status"];
    expiresAt: string;
  };
  load: {
    _id: string;
    reference: string;
    origin: { city: string; state: string };
    destination: { city: string; state: string };
    pickupWindowStart: string;
    deliveryWindowStart: string;
    equipmentType: string;
  };
}

const getByToken = makeFunctionReference<
  "query",
  { tenantId: string; token: string },
  CarrierOfferQueryResult | null
>("carrierLoadOffers:getByToken");
const respond = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    token: string;
    responseType: CarrierOfferResponseType;
    signerName: string;
    note?: string;
    counterAmountCents?: number;
    payload?: Record<string, unknown>;
  },
  { status: string; actionId?: string; approvalId?: string; blockedReasons?: string[] }
>("carrierLoadOffers:respond");
const uploadDocument = makeFunctionReference<
  "mutation",
  {
    tenantId: string;
    token: string;
    documentType: CarrierPortalDocumentType;
    fileName: string;
    rawText?: string;
  },
  { documentId: string; evidenceId: string; approvalId?: string }
>("carrierDocuments:uploadViaOfferToken");

export function CarrierLoadOfferPortal({
  tenantId,
  token,
}: {
  tenantId: string;
  token: string;
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) return <CarrierLoadOfferContent tenantId={tenantId} data={demoOffer(token)} />;
  return <CarrierLoadOfferLive tenantId={tenantId} token={token} />;
}

function CarrierLoadOfferLive({ tenantId, token }: { tenantId: string; token: string }) {
  const data = useQuery(getByToken, { tenantId, token });
  const respondMutation = useMutation(respond);
  const uploadMutation = useMutation(uploadDocument);
  if (data === undefined) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-8">
        <QuerySkeleton surface="carrierOffer" />
      </main>
    );
  }
  if (!data) return <main className="mx-auto max-w-3xl px-5 py-8 text-sm text-muted-foreground">Offer not found.</main>;
  return (
    <CarrierLoadOfferContent
      tenantId={tenantId}
      data={data}
      onRespond={(input) => respondMutation({ tenantId, token, ...input })}
      onUpload={(input) => uploadMutation({ tenantId, token, ...input })}
    />
  );
}

function CarrierLoadOfferContent({
  tenantId,
  data,
  onRespond,
  onUpload,
}: {
  tenantId: string;
  data: CarrierOfferQueryResult;
  onRespond?: (input: {
    responseType: CarrierOfferResponseType;
    signerName: string;
    note?: string;
    counterAmountCents?: number;
    payload?: Record<string, unknown>;
  }) => Promise<{ status: string; blockedReasons?: string[] }>;
  onUpload?: (input: {
    documentType: CarrierPortalDocumentType;
    fileName: string;
    rawText?: string;
  }) => Promise<{ documentId: string; evidenceId: string; approvalId?: string }>;
}) {
  const [signerName, setSignerName] = useState("");
  const [note, setNote] = useState("");
  const [counterDollars, setCounterDollars] = useState("");
  const [documentType, setDocumentType] = useState<CarrierPortalDocumentType>("pod");
  const [fileName, setFileName] = useState("");
  const [rawText, setRawText] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const offer = toCarrierOffer(tenantId, data);

  async function submitResponse(responseType: CarrierOfferResponseType) {
    const counterAmountCents = counterDollars ? Math.round(Number(counterDollars) * 100) : undefined;
    const draft = buildCarrierOfferResponseDraft({
      offer,
      responseType,
      signerName,
      nowIso: new Date().toISOString(),
      note,
      counterAmountCents,
    });
    if (!draft.ready) {
      setMessage(draft.blockedReasons.join("; "));
      return;
    }
    const result = await onRespond?.({ responseType, signerName, note, counterAmountCents, payload: { draft } });
    if (result?.status === "blocked") {
      setMessage(result.blockedReasons?.join("; ") ?? "Response blocked");
      return;
    }
    setMessage(`${responseType} response staged for dispatcher review.`);
  }

  async function submitDocument() {
    if (!fileName) {
      setMessage("Document file name required.");
      return;
    }
    const draft = buildCarrierDocumentUploadDraft({
      loadReference: data.load.reference,
      documentType,
      fileName,
      rawText,
    });
    await onUpload?.({ documentType, fileName, rawText });
    setMessage(
      `${documentType.replaceAll("_", " ")} uploaded with ${draft.ocrStatus}${draft.draftOnly ? " and held for review" : ""}.`,
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-5 py-8">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Carrier Load Offer</h1>
          <p className="text-sm text-muted-foreground">Signed-link tender response portal.</p>
        </div>
        <Badge variant={data.offer.status === "pending" ? "info" : "warn"}>{data.offer.status}</Badge>
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Card>
          <CardHeader>
            <CardTitle>{data.load.reference}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <Mini label="Origin" value={`${data.load.origin.city}, ${data.load.origin.state}`} />
              <Mini label="Destination" value={`${data.load.destination.city}, ${data.load.destination.state}`} />
              <Mini label="Pickup" value={data.load.pickupWindowStart} />
              <Mini label="Delivery" value={data.load.deliveryWindowStart} />
              <Mini label="Equipment" value={data.load.equipmentType} />
              <Mini label="Carrier pay" value={formatCarrierPay(data.offer.carrierPayCents)} />
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Signer name</span>
              <input
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm outline-none"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Note</span>
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                className="min-h-20 w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none"
              />
            </label>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Respond</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full justify-start" onClick={() => submitResponse("accept")}>
              <Check size={16} />
              Accept
            </Button>
            <Button variant="secondary" className="w-full justify-start" onClick={() => submitResponse("decline")}>
              <X size={16} />
              Decline
            </Button>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <input
                value={counterDollars}
                onChange={(event) => setCounterDollars(event.target.value)}
                placeholder="Counter $"
                className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
              />
              <Button variant="secondary" onClick={() => submitResponse("counter")}>
                <DollarSign size={16} />
                Counter
              </Button>
            </div>
            <div className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
              Acceptance and counters are held for dispatcher review before any load commitment changes.
            </div>
            {message ? (
              <div className="flex gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
                <Send size={16} />
                {message}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Documents</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-[180px_1fr_1fr_auto]">
          <select
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value as CarrierPortalDocumentType)}
            className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
          >
            <option value="pod">POD</option>
            <option value="bol">BOL</option>
            <option value="lumper_receipt">Lumper receipt</option>
          </select>
          <input
            value={fileName}
            onChange={(event) => setFileName(event.target.value)}
            placeholder="File name"
            className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
          />
          <input
            value={rawText}
            onChange={(event) => setRawText(event.target.value)}
            placeholder="OCR text sample"
            className="h-10 rounded-md border border-border bg-card px-3 text-sm outline-none"
          />
          <Button variant="secondary" onClick={submitDocument}>
            Upload
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}

function toCarrierOffer(tenantId: string, data: CarrierOfferQueryResult): CarrierOffer {
  return {
    tenantId,
    token: data.offer.token,
    carrierName: data.offer.carrierName,
    carrierMc: data.offer.carrierMc,
    carrierEmail: data.offer.carrierEmail,
    status: data.offer.status,
    expiresAt: data.offer.expiresAt,
    load: {
      loadId: data.load._id,
      reference: data.load.reference,
      origin: data.load.origin,
      destination: data.load.destination,
      pickupWindowStart: data.load.pickupWindowStart,
      deliveryWindowStart: data.load.deliveryWindowStart,
      equipmentType: data.load.equipmentType,
      carrierPayCents: data.offer.carrierPayCents,
    },
  };
}

function demoOffer(token: string): CarrierOfferQueryResult {
  return {
    offer: {
      token,
      carrierName: "North Star Trucking",
      carrierMc: "123456",
      carrierEmail: "dispatch@northstar.example",
      carrierPayCents: 255000,
      status: "pending",
      expiresAt: "2026-05-14T12:00:00.000Z",
    },
    load: {
      _id: "load_1001",
      reference: "HB-1001",
      origin: { city: "Dallas", state: "TX" },
      destination: { city: "Atlanta", state: "GA" },
      pickupWindowStart: "2026-05-13T18:00:00.000Z",
      deliveryWindowStart: "2026-05-14T18:00:00.000Z",
      equipmentType: "Dry van",
    },
  };
}
